import { Box, Button, Chip, styled, TextField, Typography } from 'opensolar-ui'
import React, { useState } from 'react'
import { useTranslate } from 'react-admin'

interface CustomFeedbackConfig {
  enabled: boolean
  text: string
  placeholder: string
  rows: number
  onChange: (text: string) => void
  customChipKey?: string
}

interface ChipSelectionProps {
  selectedChips: string[]
  onChipToggle: (chip: string) => void
  onSubmit: () => void
  onCancel: () => void
  isSubmitting: boolean
  feedbackOptions?: string[]
  questionText?: string
  customFeedback?: CustomFeedbackConfig
  onCustomFieldShown?: () => void
}

const Container = styled('div')`
  margin-top: 12px;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
`

const QuestionText = styled(Typography)`
  color: rgba(0, 0, 0, 0.7);
  font-size: 12px;
  margin-bottom: 8px;
`

const ChipsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => !['$isSelected'].includes(prop as string),
})<{ $isSelected: boolean }>`
  background-color: ${(props) => (props.$isSelected ? '#4272dd' : '#ffffff')} !important;
  color: ${(props) => (props.$isSelected ? '#ffffff' : '#666666')} !important;
  border: 1px solid #e0e0e0;
  font-size: 12px;
  height: 30px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${(props) => (props.$isSelected ? '#3a5bb8' : '#4272dd')} !important;
    color: #ffffff !important;
  }
`

const ButtonsContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 5px;
  margin-top: 8px;
`

const CancelButton = styled(Button)`
  color: #666666;
  background-color: transparent;
  border: none;
  padding: 4px 12px;
  font-size: 12px;
  text-transform: none;
  min-width: auto;
  cursor: pointer;
`

const SubmitButton = styled(Button, {
  shouldForwardProp: (prop) => !['$isDisabled'].includes(prop as string),
})<{ $isDisabled: boolean }>`
  background-color: ${(props) => (props.$isDisabled ? '#e0e0e0' : '#4272DD')};
  color: ${(props) => (props.$isDisabled ? '#9e9e9e' : '#ffffff')};
  padding: 4px 12px;
  font-size: 12px;
  text-transform: none;
  min-width: auto;
  border-radius: 4px;
  cursor: ${(props) => (props.$isDisabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${(props) => (props.$isDisabled ? '#e0e0e0' : '#3a5bb8')};
  }
`

const CustomTextField = styled(TextField)`
  margin-top: 12px;
  width: 100%;

  input,
  textarea {
    font-size: 13px !important;
  }

  .MuiOutlinedInput-root {
    border-radius: 8px;
  }
`

export const ChipSelection: React.FC<ChipSelectionProps> = ({
  selectedChips,
  onChipToggle,
  onSubmit,
  onCancel,
  isSubmitting,
  feedbackOptions = [],
  questionText,
  customFeedback,
  onCustomFieldShown,
}) => {
  const customChipKey = customFeedback?.customChipKey || 'Something Else'
  const isSubmitDisabled =
    selectedChips.length === 0 ||
    isSubmitting ||
    (customFeedback?.enabled && selectedChips.includes(customChipKey) && !customFeedback?.text?.trim())
  const translate = useTranslate()
  const [showCustomField, setShowCustomField] = useState(false)

  const allFeedbackOptions = customFeedback?.enabled ? [...feedbackOptions, customChipKey] : feedbackOptions

  const handleChipToggle = (chip: string) => {
    if (chip === customChipKey && customFeedback?.enabled) {
      const wasShowing = showCustomField
      setShowCustomField(!showCustomField)
      onChipToggle(chip)
      if (wasShowing) {
        customFeedback.onChange('')
      } else {
        // Custom field is being shown, trigger callback
        setTimeout(() => {
          onCustomFieldShown?.()
        }, 0)
      }
    } else {
      onChipToggle(chip)
    }
  }

  if (feedbackOptions.length === 0) {
    onSubmit()
    return null
  }

  return (
    <Container>
      <Box display="flex" flexDirection="column" gridGap={2}>
        <QuestionText variant="body2">
          {questionText || translate('What went wrong? Select all that apply:')}
        </QuestionText>
        <ChipsContainer>
          {allFeedbackOptions.map((option) => (
            <StyledChip
              key={option}
              label={option}
              size="medium"
              clickable
              onClick={() => handleChipToggle(option)}
              $isSelected={selectedChips.includes(option)}
            />
          ))}
        </ChipsContainer>

        {showCustomField && customFeedback && (
          <CustomTextField
            multiline
            rows={customFeedback.rows}
            value={customFeedback.text}
            onChange={(e) => customFeedback.onChange(e.target.value)}
            placeholder={customFeedback.placeholder}
            variant="outlined"
            size="small"
          />
        )}
        <ButtonsContainer>
          <CancelButton onClick={onCancel} size="small">
            {translate('Cancel')}
          </CancelButton>
          <SubmitButton onClick={onSubmit} disabled={!!isSubmitDisabled} size="small" $isDisabled={!!isSubmitDisabled}>
            {translate('Submit')}
          </SubmitButton>
        </ButtonsContainer>
      </Box>
    </Container>
  )
}
